import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Plus, Rocket, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { getCampaigns } from '../services/api';
import { useChannel } from '../context/ChannelContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useCampaignsConfig } from '../hooks/useCampaignsConfig';
import { CampaignStatusBadge } from '../components/campaigns/CampaignStatusBadge';
import NewCampaignDialog from '../components/campaigns/NewCampaignDialog';
import { scheduleText, relative } from '../components/campaigns/scheduleText';

const PRO_REASON = 'Campaigns are a Pro feature';

export const Campaigns = () => {
  const { selectedChannelId, selectedChannel, channels } = useChannel();
  const { openProDialog } = useWorkspace();
  const config = useCampaignsConfig();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!selectedChannelId) {
      setCampaigns([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getCampaigns(selectedChannelId)
      .then((data) => { if (!cancelled) setCampaigns(data.campaigns || []); })
      .catch((error) => {
        if (!cancelled) {
          setCampaigns([]);
          toast.error(error.response?.data?.detail || 'Failed to load campaigns');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedChannelId]);

  if (!config.loading && !config.visible) return <Navigate to="/menus" replace />;

  if (!selectedChannelId && channels.length === 0) {
    return (
      <div className="p-4 sm:p-6" data-testid="campaigns-page">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-[#18181B] flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-yellow-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No channels configured</h3>
          <p className="text-[#A1A1AA] text-sm mb-6 max-w-sm">
            Connect a LINE channel to start launching campaigns.
          </p>
        </div>
      </div>
    );
  }

  const isProGate = !config.loading && !config.allowed && config.reason === PRO_REASON;
  const canCreate = config.allowed && !!selectedChannelId;

  return (
    <div className="p-4 sm:p-6" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Campaigns</h1>
          <p className="text-[#A1A1AA] text-sm mt-1" data-testid="campaigns-channel">
            {selectedChannel ? (
              <>Campaigns for <span className="text-[#06C755]">{selectedChannel.name}</span></>
            ) : (
              'Select a channel to view campaigns'
            )}
          </p>
          <p className="text-[#52525B] text-xs mt-1">
            Launch a set of linked menus together — now or at a scheduled time. If anything fails, LINE is put back as it was.
          </p>
        </div>
        <Button
          className="btn-primary w-full sm:w-auto"
          onClick={() => (isProGate ? openProDialog() : setDialogOpen(true))}
          disabled={!selectedChannelId || (!canCreate && !isProGate)}
          data-testid="new-campaign-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New campaign
        </Button>
      </div>

      {isProGate && (
        <div className="mb-6 p-5 bg-amber-500/10 border border-amber-500/20 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="campaigns-pro-upsell">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Campaigns are a Pro feature</p>
              <p className="text-xs text-[#A1A1AA]">Upgrade to launch and schedule linked menus with automatic rollback.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" onClick={openProDialog}>
            Upgrade to Pro
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#00E05E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="empty-state" data-testid="campaigns-empty">
          <div className="w-16 h-16 rounded-full bg-[#18181B] flex items-center justify-center mb-4">
            <Rocket className="w-8 h-8 text-[#52525B]" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
          <p className="text-[#A1A1AA] text-sm mb-6">
            {selectedChannel ? <>Create your first campaign for <span className="text-[#06C755]">{selectedChannel.name}</span></> : 'Create your first campaign'}
          </p>
          <Button
            className="btn-primary"
            onClick={() => (isProGate ? openProDialog() : setDialogOpen(true))}
            disabled={!canCreate && !isProGate}
          >
            New campaign
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {campaigns.map((c) => (
            <div
              key={c.id}
              className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 hover:border-[#3F3F46] transition-colors"
              data-testid={`campaign-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <Link to={`/campaigns/${c.id}`} className="font-semibold hover:text-[#00E05E] truncate">
                  {c.name}
                </Link>
                <CampaignStatusBadge status={c.status} />
              </div>
              <p className="text-sm text-[#A1A1AA] mt-2">
                {(c.menuIds || []).length} {(c.menuIds || []).length === 1 ? 'menu' : 'menus'}
              </p>
              {c.status === 'scheduled' && c.schedule && (
                <p className="text-xs text-[#6366F1] mt-1">{scheduleText(c.schedule)}</p>
              )}
              {c.updatedAt && (
                <p className="text-xs text-[#52525B] mt-1">Updated {relative(new Date(c.updatedAt))}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <NewCampaignDialog open={dialogOpen} onOpenChange={setDialogOpen} channelId={selectedChannelId} />
    </div>
  );
};

export default Campaigns;
